<template>
  <div
    ref="wrapRef"
    class="sidebar-user-card-wrap"
  >
    <Teleport
      to="body"
      :disabled="!useFloatingMenu"
    >
      <div
        ref="menuRef"
        class="sidebar-user-menu"
        :class="{
          'sidebar-user-menu--open': menuOpen,
          'sidebar-user-menu--rail': isRailMode,
          'sidebar-user-menu--floating': useFloatingMenu
        }"
        :style="floatingMenuStyle"
        role="menu"
        aria-label="Account menu"
        :inert="!menuOpen"
        @keydown="handleMenuKeydown"
      >
        <div class="sidebar-user-menu__title">Account</div>
        <button
          v-for="item in accountMenuActions"
          :key="item.label"
          type="button"
          class="sidebar-user-menu__item"
          role="menuitem"
          @click="handleAction(item)"
        >
          <i
            :class="item.icon"
            class="sidebar-user-menu__item-icon"
            aria-hidden="true"
          />
          <span class="sidebar-user-menu__item-label">{{ item.label }}</span>
        </button>

        <div class="sidebar-user-menu__divider" role="separator" />
        <div class="sidebar-user-menu__title">Appearance</div>
        <div class="sidebar-user-menu__item sidebar-user-menu__item--switch">
          <i
            :class="isDark ? 'pi pi-moon' : 'pi pi-sun'"
            class="sidebar-user-menu__item-icon"
            aria-hidden="true"
          />
          <span class="sidebar-user-menu__item-label">Dark mode</span>
          <button
            type="button"
            class="sidebar-user-menu__switch"
            role="switch"
            :aria-checked="isDark ? 'true' : 'false'"
            aria-label="Toggle dark mode"
            @click.stop="handleToggleTheme"
          />
        </div>

        <template v-if="showSidebarBehaviorControls">
          <div class="sidebar-user-menu__divider" role="separator" />
          <div class="sidebar-user-menu__title">Sidebar behavior</div>
          <div class="sidebar-user-menu__item sidebar-user-menu__item--switch">
            <i
              class="pi pi-arrow-right-arrow-left sidebar-user-menu__item-icon"
              aria-hidden="true"
            />
            <span class="sidebar-user-menu__item-label">Expand on hover</span>
            <button
              type="button"
              class="sidebar-user-menu__switch"
              role="switch"
              :aria-checked="hoverExpandEnabled ? 'true' : 'false'"
              aria-label="Expand collapsed sidebar on hover"
              @click.stop="handleToggleHoverExpand"
            />
          </div>
          <p class="sidebar-user-menu__note">
            Hover expansion is an overlay, so page content never jumps sideways.
          </p>
        </template>

        <div class="sidebar-user-menu__divider" role="separator" />

        <button
          type="button"
          class="sidebar-user-menu__item"
          role="menuitem"
          @click="handleLogout"
        >
          <i
            class="pi pi-sign-out sidebar-user-menu__item-icon"
            aria-hidden="true"
          />
          <span class="sidebar-user-menu__item-label">Log out</span>
        </button>
      </div>
    </Teleport>

    <button
      type="button"
      ref="triggerRef"
      class="sidebar-user-card"
      aria-haspopup="menu"
      :aria-expanded="menuOpen ? 'true' : 'false'"
      aria-label="Account menu"
      v-tooltip.right="tooltipLabel"
      @click="toggleMenu"
      @keydown.down.prevent="openMenuWithKeyboard"
      @keydown.up.prevent="openMenuWithKeyboard($event, true)"
    >
      <span class="sidebar-user-card__avatar" aria-hidden="true">
        <span class="sidebar-user-card__avatar-surface">
          <img
            v-if="showAvatar"
            :src="userCard.avatarUrl"
            alt=""
            class="sidebar-user-card__avatar-img"
            @error="avatarFailed = true"
          />
          <template v-else>{{ userCard.initials }}</template>
        </span>
      </span>
      <span class="sidebar-user-card__copy">
        <span class="sidebar-user-card__name">{{ userCard.name }}</span>
        <span class="sidebar-user-card__role">{{ userCard.subtitle }}</span>
      </span>
      <i
        class="pi pi-ellipsis-v sidebar-user-card__menu-icon"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<script>
import { useAppTheme } from '@/composables/useAppTheme';

export default {
  name: 'SidebarUserCard',
  props: {
    userCard: {
      type: Object,
      required: true
    },
    accountMenuActions: {
      type: Array,
      default: () => []
    },
    showSidebarBehaviorControls: {
      type: Boolean,
      default: false
    },
    hoverExpandEnabled: {
      type: Boolean,
      default: false
    },
    isSidebarVisible: {
      type: Boolean,
      default: true
    },
    isRailMode: {
      type: Boolean,
      default: false
    }
  },
  emits: ['menu-open-change', 'toggle-hover-expand', 'logout'],
  setup() {
    const { isDark, toggleTheme } = useAppTheme();
    return { isDark, toggleTheme };
  },
  data() {
    return {
      menuOpen: false,
      floatingMenuStyle: null,
      scrollParent: null,
      avatarFailed: false
    };
  },
  computed: {
    tooltipLabel() {
      return this.isRailMode ? 'Account' : null;
    },
    useFloatingMenu() {
      return this.isRailMode;
    },
    showAvatar() {
      return Boolean(this.userCard?.avatarUrl) && !this.avatarFailed;
    }
  },
  watch: {
    isSidebarVisible(visible) {
      if (!visible) this.setMenuOpen(false);
    },
    '$route.fullPath'() {
      this.setMenuOpen(false);
    },
    'userCard.avatarUrl'() {
      this.avatarFailed = false;
    },
    isRailMode() {
      if (!this.menuOpen) return;
      this.$nextTick(() => {
        this.updateFloatingMenuPosition();
      });
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.handleWindowChange);
    this.scrollParent = this.findScrollParent();
    this.scrollParent?.addEventListener('scroll', this.handleWindowChange, { passive: true });
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.handleWindowChange);
    this.scrollParent?.removeEventListener('scroll', this.handleWindowChange);
    if (this.menuOpen) this.$emit('menu-open-change', false);
  },
  methods: {
    findScrollParent() {
      let element = this.$refs.wrapRef?.parentElement;
      while (element) {
        if (element.classList?.contains('sidebar-root__scroll')) {
          return element;
        }
        element = element.parentElement;
      }
      return null;
    },
    updateFloatingMenuPosition() {
      if (!this.useFloatingMenu) {
        this.floatingMenuStyle = null;
        return;
      }

      const wrap = this.$refs.wrapRef;
      if (!wrap) return;

      const rect = wrap.getBoundingClientRect();
      const gap = 8;
      const bottomOffset = 10;

      this.floatingMenuStyle = {
        left: `${Math.round(rect.right + gap)}px`,
        bottom: `${Math.round(window.innerHeight - rect.bottom + bottomOffset)}px`
      };
    },
    handleWindowChange() {
      if (!this.menuOpen || !this.useFloatingMenu) return;
      this.updateFloatingMenuPosition();
    },
    toggleMenu(event) {
      event.stopPropagation();
      this.setMenuOpen(!this.menuOpen);
    },
    openMenuWithKeyboard(event, last = false) {
      if (this.menuOpen) this.focusMenuButton(last);
      else this.setMenuOpen(true, last);
    },
    focusMenuButton(last = false) {
      this.$nextTick(() => {
        // Wait for the visibility change to paint before focusing a formerly hidden menu.
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (!this.menuOpen) return;
          const buttons = this.menuButtons();
          buttons[last ? buttons.length - 1 : 0]?.focus();
        }));
      });
    },
    menuButtons() {
      return Array.from(this.$refs.menuRef?.querySelectorAll('button:not(:disabled)') || []);
    },
    handleMenuKeydown(event) {
      if (!this.menuOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.setMenuOpen(false);
        this.$refs.triggerRef?.focus();
        return;
      }
      const buttons = this.menuButtons();
      const index = buttons.indexOf(document.activeElement);
      let next;
      if (event.key === 'ArrowDown') next = (index + 1) % buttons.length;
      else if (event.key === 'ArrowUp') next = (index - 1 + buttons.length) % buttons.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = buttons.length - 1;
      else if (event.key === 'Tab') {
        // Return to the trigger before the browser advances focus out of the menu.
        this.$refs.triggerRef?.focus();
        this.setMenuOpen(false);
        return;
      } else return;
      event.preventDefault();
      event.stopPropagation();
      buttons[next]?.focus();
    },
    setMenuOpen(open, focusLast = false) {
      if (this.menuOpen === open) return;
      this.menuOpen = open;
      if (open) {
        this.$nextTick(() => {
          this.updateFloatingMenuPosition();
          this.focusMenuButton(focusLast);
        });
      } else {
        this.floatingMenuStyle = null;
      }
      this.$emit('menu-open-change', open);
    },
    handleAction(item) {
      item?.command?.();
      this.setMenuOpen(false);
    },
    handleToggleTheme() {
      this.toggleTheme();
    },
    handleToggleHoverExpand() {
      this.$emit('toggle-hover-expand');
    },
    handleLogout() {
      this.setMenuOpen(false);
      this.$emit('logout');
    },
    handleDocumentClick(event) {
      if (!this.menuOpen) return;
      const wrap = this.$refs.wrapRef;
      const menu = this.$refs.menuRef;
      if (wrap?.contains(event.target) || menu?.contains(event.target)) {
        return;
      }
      this.setMenuOpen(false);
    }
  }
};
</script>

<style scoped>
.sidebar-user-card-wrap {
  position: relative;
  width: 100%;
}

.sidebar-user-menu {
  position: absolute;
  left: 0.625rem;
  right: 0.625rem;
  bottom: calc(100% + 0.5rem);
  padding: 0.5rem;
  max-height: calc(100dvh - 6rem);
  overflow-y: auto;
  overscroll-behavior: contain;
  border: 1px solid var(--crc-sidebar-line);
  border-radius: 0.875rem;
  background: var(--p-toolbar-background);
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.18);
  opacity: 0;
  visibility: hidden;
  transform: translateY(0.5rem) scale(0.985);
  transform-origin: bottom center;
  transition: opacity 0.16s ease, transform 0.16s ease;
  z-index: 1100;
}

.sidebar-user-menu--floating {
  position: fixed;
  right: auto;
  width: 15.5rem;
  transform-origin: left bottom;
}

.sidebar-user-menu--rail:not(.sidebar-user-menu--floating) {
  left: calc(100% + 0.5rem);
  right: auto;
  bottom: 0.625rem;
  width: 15.5rem;
  transform-origin: left bottom;
}

.sidebar-user-menu--open {
  opacity: 1;
  visibility: visible;
  transform: none;
}

.sidebar-user-menu--rail.sidebar-user-menu--open,
.sidebar-user-menu--floating.sidebar-user-menu--open {
  transform: none;
}

.sidebar-user-menu__title {
  padding: 0.5rem 0.625rem 0.375rem;
  color: var(--crc-sidebar-text-muted);
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.sidebar-user-menu__item {
  width: 100%;
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0 0.625rem;
  border: 0;
  border-radius: 0.5625rem;
  background: transparent;
  color: var(--crc-sidebar-text);
  text-align: left;
  cursor: pointer;
}

.sidebar-user-menu__item:hover {
  background: var(--crc-sidebar-link-hover-bg);
}

.sidebar-user-menu__item:focus-visible {
  outline: 2px solid var(--crc-sidebar-active-icon);
  outline-offset: 1px;
}

.sidebar-user-menu__item--switch {
  cursor: default;
}

.sidebar-user-menu__item--switch:hover {
  background: transparent;
}

.sidebar-user-menu__item-icon {
  width: 1.0625rem;
  height: 1.0625rem;
  flex: 0 0 1.0625rem;
  display: grid;
  place-items: center;
  color: var(--crc-sidebar-text-muted);
  font-size: 0.9375rem;
}

.sidebar-user-menu__item-label {
  min-width: 0;
  flex: 1 1 auto;
  font-size: 0.75rem;
  font-weight: 620;
}

.sidebar-user-menu__divider {
  height: 1px;
  margin: 0.375rem 0.25rem;
  background: var(--crc-sidebar-line);
}

.sidebar-user-menu__switch {
  position: relative;
  width: 2.25rem;
  height: 1.375rem;
  flex: 0 0 2.25rem;
  border: 0;
  border-radius: 999px;
  background: var(--crc-sidebar-switch-bg, #d8dee3);
  cursor: pointer;
  transition: background 0.16s ease;
}

.sidebar-user-menu__switch::after {
  content: '';
  position: absolute;
  top: 0.1875rem;
  left: 0.1875rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  transition: transform 0.16s ease;
}

.sidebar-user-menu__switch[aria-checked='true'] {
  background: var(--crc-sidebar-active-icon);
}

.sidebar-user-menu__switch[aria-checked='true']::after {
  transform: translateX(0.875rem);
}

.sidebar-user-menu__switch:focus-visible {
  outline: 2px solid var(--crc-sidebar-active-icon);
  outline-offset: 2px;
}

.sidebar-user-menu__note {
  margin: 0.375rem 0.625rem 0.5rem 2.3125rem;
  color: var(--crc-sidebar-text-muted);
  font-size: 0.625rem;
  line-height: 1.45;
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-user-menu,
  .sidebar-user-menu__switch,
  .sidebar-user-menu__switch::after {
    transition: none !important;
  }
}
</style>
